import { useState, useEffect, useRef } from 'react';
import { Bell, Check, Trash2, MessageCircle, DollarSign, Timer, AlertCircle, CheckCircle, X } from 'lucide-react';
import { notificationService } from '@/services/notificationService';
import { backendCommunicationService } from '@/services/backendCommunicationService';
import { useAuthStore } from '@/store/authStore';
import type { Notification } from '@/types';

export function NotificationsDropdown() {
  const { user } = useAuthStore();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch notifications from backend
  const fetchNotifications = async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      console.log('🔔 Fetching notifications for user:', user.id);
      const backendNotifications = await backendCommunicationService.getNotifications();
      console.log('🔔 Backend notifications response:', backendNotifications);
      
      // Handle both array and object responses
      const notificationsArray = Array.isArray(backendNotifications) 
        ? backendNotifications 
        : (backendNotifications?.notifications || []);
      
      // Map backend notifications to frontend format
      const mappedNotifications: Notification[] = notificationsArray.map((n: any) => {
        // Backend uses "new_message", but we can keep it as-is or map to "message"
        // Both are now in the type definition
        const notificationType = n.type as Notification['type'];
        
        return {
          id: n.id,
          userId: n.user_id,
          type: notificationType,
          title: n.title,
          message: n.message,
          data: {
            jobId: n.related_job_id,
            quoteId: n.related_quote_id,
            conversationId: n.related_conversation_id,
            messageId: n.related_message_id,
          },
          read: n.read || false,
          createdAt: new Date(n.created_at),
        };
      });
      
      console.log('🔔 Mapped notifications:', mappedNotifications);
      setNotifications(mappedNotifications);
    } catch (error: any) {
      console.error('❌ Failed to fetch notifications:', error);
      console.error('❌ Error details:', error.message, error.stack);
      // Fallback to local notifications if backend fails
      const userNotifications = notificationService.getNotificationsForUser(user.id);
      console.log('🔔 Using local notifications as fallback:', userNotifications);
      setNotifications(userNotifications);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      // Fetch notifications from backend
      fetchNotifications();
      
      // Also subscribe to local notifications for real-time updates
      const unsubscribe = notificationService.subscribe((updatedNotifications) => {
        const userNotifications = updatedNotifications.filter(n => n.userId === user.id);
        // Merge with backend notifications (backend takes precedence)
        setNotifications(prev => {
          const backendIds = new Set(prev.map(n => n.id));
          const localOnly = userNotifications.filter(n => !backendIds.has(n.id));
          return [...prev, ...localOnly];
        });
      });

      // Poll for new notifications every 30 seconds
      const pollInterval = setInterval(fetchNotifications, 30000);

      return () => {
        unsubscribe();
        clearInterval(pollInterval);
      };
    }
  }, [user]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showDropdown && dropdownRef.current && !dropdownRef.current.contains(target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'quote_accepted': return <CheckCircle className="w-4 h-4 text-emerald-500" />;
      case 'quote_confirmation_reminder': return <Timer className="w-4 h-4 text-amber-500" />;
      case 'quote_expired': return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'booking_confirmed': return <CheckCircle className="w-4 h-4 text-emerald-500" />;
      case 'company_invitation': return <AlertCircle className="w-4 h-4 text-blue-600" />;
      case 'message':
      case 'new_message': return <MessageCircle className="w-4 h-4 text-blue-500" />;
      case 'job_update': return <Bell className="w-4 h-4 text-primary-500" />;
      case 'new_quote': return <DollarSign className="w-4 h-4 text-green-500" />;
      case 'quote_rejected': return <AlertCircle className="w-4 h-4 text-orange-500" />;
      case 'payment': return <DollarSign className="w-4 h-4 text-purple-500" />;
      default: return <Bell className="w-4 h-4 text-gray-400" />;
    }
  };

  const markAsRead = async (id: string) => {
    try {
      await backendCommunicationService.markNotificationRead(id);
      // Update local state
      setNotifications(prev => 
        prev.map(n => n.id === id ? { ...n, read: true } : n)
      );
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      // Fallback to local service
      notificationService.markAsRead(id);
    }
  };

  const markAllAsRead = async () => {
    try {
      await backendCommunicationService.markAllNotificationsRead();
      // Update local state
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
      // Fallback to local service
      notifications.forEach(notif => {
        if (!notif.read) notificationService.markAsRead(notif.id);
      });
    }
  };

  const deleteNotification = async (id: string) => {
    try {
      await backendCommunicationService.deleteNotification(id);
      // Update local state
      setNotifications(prev => prev.filter(notif => notif.id !== id));
    } catch (error) {
      console.error('Failed to delete notification:', error);
      // Fallback to local removal
      setNotifications(prev => prev.filter(notif => notif.id !== id));
    }
  };

  const unreadCount = notifications.filter(notif => !notif.read).length;
  const recentNotifications = notifications.slice(0, 5);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-all relative"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 w-5 h-5 bg-primary-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {showDropdown && (
        <div className="absolute right-0 mt-2 w-96 max-w-[calc(100vw-2rem)] bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden z-50">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-gray-600" />
              <h3 className="font-semibold text-gray-900">Notifications</h3>
              {unreadCount > 0 && (
                <span className="px-2 py-0.5 bg-primary-100 text-primary-700 text-xs font-medium rounded-full">
                  {unreadCount} new
                </span>
              )}
            </div>
            <button
              onClick={() => setShowDropdown(false)}
              className="p-1 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>

          {/* Actions */}
          {unreadCount > 0 && (
            <div className="px-4 py-2 border-b border-gray-100 bg-white">
              <button
                onClick={markAllAsRead}
                className="text-xs font-medium text-primary-600 hover:text-primary-700 flex items-center gap-1"
              >
                <Check className="w-3 h-3" /> Mark all as read
              </button>
            </div>
          )}

          {/* Notifications List */}
          <div className="max-h-[400px] overflow-y-auto">
            {isLoading ? (
              <div className="p-8 text-center text-gray-500">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-3"></div>
                <p className="text-sm">Loading notifications...</p>
              </div>
            ) : recentNotifications.length > 0 ? (
              <div className="divide-y divide-gray-100">
                {recentNotifications.map(n => (
                  <div
                    key={n.id}
                    className={`p-4 flex gap-3 transition-colors ${
                      !n.read ? 'bg-blue-50/50 hover:bg-blue-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex-shrink-0 mt-0.5">
                      {getNotificationIcon(n.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-1">
                        <h4 className={`text-sm font-semibold truncate ${!n.read ? 'text-gray-900' : 'text-gray-700'}`}>
                          {n.title}
                        </h4>
                        <span className="text-xs text-gray-400 whitespace-nowrap ml-2">
                          {formatTime(n.createdAt)}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 leading-relaxed mb-2 line-clamp-2">
                        {n.message}
                      </p>

                      <div className="flex items-center gap-3">
                        {!n.read && (
                          <button
                            onClick={() => markAsRead(n.id)}
                            className="text-xs font-medium text-primary-600 hover:text-primary-700"
                          >
                            Mark read
                          </button>
                        )}
                        <button
                          onClick={() => deleteNotification(n.id)}
                          className="text-xs font-medium text-gray-400 hover:text-red-600 flex items-center gap-1 transition-colors"
                        >
                          <Trash2 className="w-3 h-3" /> Dismiss
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center text-gray-500">
                <Bell className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                <p className="text-sm">No notifications</p>
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 5 && (
            <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 text-center">
              <button
                onClick={() => setShowDropdown(false)}
                className="text-xs font-medium text-primary-600 hover:text-primary-700"
              >
                View all {notifications.length} notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function formatTime(date: Date): string {
  const now = new Date();
  const diff = now.getTime() - new Date(date).getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return new Date(date).toLocaleDateString();
}
